import React, { PureComponent, Suspense } from 'react';
import { Redirect } from 'react-router-dom';
import { Action, bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import UnauthedHeader from '../../components/UnauthedHeader';
import { Banner } from 'Common/components/Molecules/Banner';
import { Button } from 'Common/components/Atoms/Button';
import { Card } from 'Common/components/Atoms/Card';
import { ContentLoader } from 'Common/components/Atoms/ContentLoader';
import { Div } from 'Common/components/Atoms/Div';
import { Flex } from 'Common/components/Atoms/Flex';
import { FontIcon } from 'Common/components/Atoms/FontIcon';
import { InputField } from 'Common/components/Atoms/InputField';
import { Link, RouteLink } from 'Common/components/Atoms/Link';
import { Loading } from 'Common/components/Atoms/Loading';
import { Toast } from 'Common/components/Molecules/Toast';
import { Text } from 'Common/components/Atoms/Text';
import { Theme } from '@mui/material/styles';
import withTheme from '@mui/styles/withTheme';
import { actions as loginActions } from '../../reducers/login';
import { actions as accountActions } from '../../reducers/Account';
import {
  faEnvelope,
  faEye,
  faEyeSlash,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faCheck,
  faExclamationTriangle,
} from '@fortawesome/pro-solid-svg-icons';
import { retry } from '../../utils/retry';
import queryString from 'qs';
import { User } from 'models/User';
import { State as AppState } from '../../reducers/State';
import { accountSelector } from 'selectors/Account';

const PasswordStrengthBar = React.lazy(() =>
  retry(() => import('react-password-strength-bar'))
);

export interface Props {
  location: {
    search: string;
  };
  resetPassword: (
    email: string,
    password: string,
    password_confirmation: string,
    token: string
  ) => void;
  theme: Theme;
  isUpdating?: boolean;
  requestError?: string;
  requestMessage?: string;
  user: User | null;
  redirectTo2fa: boolean;
}

interface State {
  email: string;
  password: string;
  password_confirmation: string;
  showToast: boolean;
  showPassword: boolean;
  loggingIn: boolean;
  token: string;
  errors: {
    [key in FieldName]: boolean;
  };
}

type FieldName = 'email' | 'password' | 'password_confirmation' | 'token';

export class ResetPassword extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    const queryToken = Object.keys(
      queryString.parse(this.props.location.search)
    )[0];
    const token = queryToken && queryToken.substring(1, queryToken.length);

    this.state = {
      email: '',
      password: '',
      password_confirmation: '',
      showToast: false,
      showPassword: false,
      loggingIn: false,
      token: token,
      errors: {
        email: false,
        password: false,
        password_confirmation: false,
        token: false,
      },
    };
  }

  handleChange = (name: keyof State, value: string | boolean) => {
    const { errors } = this.state;
    this.setState({
      [name]: value,
      errors: { ...errors, [name]: undefined },
    } as Pick<State, keyof State>);
  };

  onKeyUpHandler = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      this.handleSubmit();
    }
  };

  handleSubmit = () => {
    const { resetPassword } = this.props;
    const { email, password, password_confirmation, token } = this.state;
    const errors = {
      email: !email,
      password: !password || password.length < 8,
      password_confirmation:
        !password_confirmation || password_confirmation !== password,
      token: !token,
    };

    if (Object.values(errors).every((i) => !i)) {
      resetPassword(email, password, password_confirmation, token);
    } else {
      this.setState({ errors });
    }
  };

  render() {
    const {
      theme,
      isUpdating,
      requestError,
      requestMessage,
      user,
      redirectTo2fa,
    } = this.props;
    const {
      email,
      password,
      password_confirmation,
      showToast,
      errors,
      showPassword,
      token,
    } = this.state;

    // TODO: Add these redirects to the resetPassword saga
    if (user) {
      return <Redirect to="/dash" />;
    } else if (redirectTo2fa) {
      return <Redirect to="/login/verify" />;
    }

    return (
      <Div height="100vh" width="100vw" style={{ overflowY: 'auto' }}>
        <UnauthedHeader />
        <Text variant="h1" textAlign="center" mt={10} mb={4}>
          Reset Password
        </Text>
        {!token && (
          <Banner
            id="errorBanner"
            mx="auto"
            mb={3}
            color={theme.palette.error.main}
            icon={faExclamationTriangle}
            width={420}
          >
            <Text variant="body1" ml={2}>
              It looks like you've arrived at this page in error. Request a
              password reset <RouteLink to="/forgot-password">here</RouteLink>{' '}
              and check your email for a valid link.
            </Text>
          </Banner>
        )}
        <Card display="block" width={400} m="auto" py={4} px={5} mb={6}>
          <form onSubmit={this.handleSubmit}>
            <Div mb={3}>
              <Text
                variant="body1"
                mb={1}
                color={token ? 'textPrimary' : 'textSecondary'}
              >
                Email Address:
              </Text>
              <InputField
                id="email"
                fullWidth
                type="email"
                required
                placeholder="Email address"
                value={email}
                startIcon={
                  <FontIcon
                    icon={faEnvelope}
                    size="lg"
                    color={theme.palette.grey[500]}
                  />
                }
                onChange={(event) =>
                  this.handleChange('email', event.target.value)
                }
                error={errors?.email}
                helperText={
                  errors?.email && 'Please provide a valid email address.'
                }
                onKeyUp={this.onKeyUpHandler}
                disabled={!token}
              />
            </Div>
            <Div mb={3}>
              <Text
                variant="body1"
                mb={1}
                color={token ? 'textPrimary' : 'textSecondary'}
              >
                New Password:
              </Text>
              <InputField
                id="password"
                fullWidth
                type={showPassword ? 'text' : 'password'}
                required
                placeholder="At least 8 characters"
                value={password}
                endIcon={
                  <FontIcon
                    icon={showPassword ? faEyeSlash : faEye}
                    color={theme.palette.text.secondary}
                    onClick={() =>
                      this.setState({ showPassword: !showPassword })
                    }
                    cursor="pointer"
                    size="sm"
                  />
                }
                onChange={(event) =>
                  this.handleChange('password', event.target.value)
                }
                error={errors?.password}
                helperText={
                  errors?.password &&
                  'Your new password must be at least 8 characters.'
                }
                onKeyUp={this.onKeyUpHandler}
                disabled={!token}
              />
              <Suspense
                fallback={
                  password ? (
                    <Div height={26}>
                      <ContentLoader height="2px" width="100%" />
                    </Div>
                  ) : null
                }
              >
                {password && (
                  <PasswordStrengthBar password={password} minLength={8} />
                )}
              </Suspense>
            </Div>
            <Div mb={3}>
              <Text
                variant="body1"
                mb={1}
                color={token ? 'textPrimary' : 'textSecondary'}
              >
                Confirm New Password:
              </Text>
              <InputField
                id="password_confirmation"
                fullWidth
                type={showPassword ? 'text' : 'password'}
                required
                placeholder="Passwords must match!"
                value={password_confirmation}
                endIcon={
                  <FontIcon
                    icon={showPassword ? faEyeSlash : faEye}
                    color={theme.palette.text.secondary}
                    onClick={() =>
                      this.setState({ showPassword: !showPassword })
                    }
                    cursor="pointer"
                    size="sm"
                  />
                }
                onChange={(event) =>
                  this.handleChange('password_confirmation', event.target.value)
                }
                error={errors?.password_confirmation}
                helperText={
                  errors?.password_confirmation && 'Passwords must match.'
                }
                onKeyUp={this.onKeyUpHandler}
                disabled={!token}
              />
            </Div>
            {(requestError || requestMessage || errors.token) && (
              <Text variant="body1" color="error" pt={1} textAlign="center">
                {requestError ||
                  requestMessage ||
                  'No password reset token supplied.'}
              </Text>
            )}
            <Button
              mt={3}
              variant="contained"
              color="primary"
              onClick={this.handleSubmit}
              width="100%"
              disabled={!email || !password || !password_confirmation}
            >
              {isUpdating ? (
                <Flex justifyContent="center" alignItems="center">
                  <Loading size={24} customcolor={theme.palette.common.white} />
                </Flex>
              ) : (
                `Reset Password`
              )}
            </Button>
            <Div
              mt={3}
              pt={3}
              borderTop={`1px solid ${theme.palette.grey[300]}`}
            >
              <Text
                variant="body1"
                color="textSecondary"
                textAlign="center"
                fontWeight={theme.typography.fontWeightLight}
              >
                Remembered your password?{' '}
                <Link href="/login" color="secondary">
                  Sign in
                </Link>
              </Text>
            </Div>
          </form>
        </Card>
        <Toast
          severity="success"
          showToast={showToast}
          handleClose={() => this.setState({ showToast: false })}
          message={`Password reset successful. Logging you in to PullRequest now.`}
          horizontal="center"
          vertical="bottom"
          icon={faCheck}
        />
      </Div>
    );
  }
}

function mapStateToProps(state: AppState) {
  const { login } = state;
  const account = accountSelector(state);
  return {
    isUpdating: login?.isUpdating,
    requestError: login?.error,
    requestMessage: login?.message,
    user: account?.user,
    redirectTo2fa: !!login?.twoFactorChallengeToken,
  };
}

function mapDispatchToProps(dispatch: Dispatch<Action>) {
  return bindActionCreators({ ...loginActions, ...accountActions }, dispatch);
}

export default withTheme<Theme, React.JSXElementConstructor<Props>>(
  connect(mapStateToProps, mapDispatchToProps)(ResetPassword)
);
